import {
	BASE_NETWORK_RETRY_DELAY_MS,
	DEFAULT_BACKGROUND_RETRIES,
	inferNetworkRequestIntentFromMethod,
	isNetworkConnectivityError,
	showActiveNetworkErrorToast,
	type NetworkRequestIntent,
} from "./network-behavior"

interface FetchWithNetworkBehaviorOptions {
	networkIntent?: NetworkRequestIntent
	backgroundRetries?: number
	suppressOfflineToast?: boolean
}

const isAbortError = (error: unknown): boolean => {
	return error instanceof DOMException
		? error.name === "AbortError"
		: error instanceof Error
			? error.name === "AbortError"
			: false
}

const buildAbortError = (): Error => {
	return new DOMException("The operation was aborted.", "AbortError")
}

const waitForRetryDelay = async (retryCount: number, signal?: AbortSignal): Promise<void> => {
	if (retryCount <= 0) {
		return
	}

	const delayMs = BASE_NETWORK_RETRY_DELAY_MS * 2 ** (retryCount - 1)

	await new Promise<void>((resolve, reject) => {
		if (signal?.aborted) {
			reject(signal.reason ?? buildAbortError())
			return
		}

		const timer = globalThis.setTimeout(() => {
			signal?.removeEventListener("abort", onAbort)
			resolve()
		}, delayMs)

		const onAbort = () => {
			globalThis.clearTimeout(timer)
			signal?.removeEventListener("abort", onAbort)
			reject(signal?.reason ?? buildAbortError())
		}

		signal?.addEventListener("abort", onAbort, { once: true })
	})
}

export const fetchWithNetworkBehavior = async (
	input: RequestInfo | URL,
	init?: RequestInit,
	options?: FetchWithNetworkBehaviorOptions,
): Promise<Response> => {
	const networkIntent =
		options?.networkIntent ?? inferNetworkRequestIntentFromMethod(init?.method)
	const backgroundRetries = Math.max(0, options?.backgroundRetries ?? DEFAULT_BACKGROUND_RETRIES)
	const maxRetries = networkIntent === "background" ? backgroundRetries : 0

	for (let retryCount = 0; ; retryCount += 1) {
		try {
			return await fetch(input, init)
		} catch (error) {
			if (isAbortError(error)) {
				throw error
			}

			const isConnectivityError = isNetworkConnectivityError(error)
			const shouldRetry =
				networkIntent === "background" && isConnectivityError && retryCount < maxRetries

			if (shouldRetry) {
				await waitForRetryDelay(retryCount + 1, init?.signal ?? undefined)
				continue
			}

			if (
				networkIntent === "active" &&
				isConnectivityError &&
				!options?.suppressOfflineToast
			) {
				showActiveNetworkErrorToast()
			}

			throw error
		}
	}
}
